import React, { useEffect, useMemo, useState, useCallback } from "react";
import { BellRinging, Plus, Trash, X, Check, ClockClockwise, User, CalendarBlank } from "@phosphor-icons/react";
import axios from "../../utils/axiosInstance";
import { useAuth } from "../../contexts/AuthContext";

const API = `${window.location.origin}/api`;

// Formats a Date object to "YYYY-MM-DDTHH:MM" for datetime-local input (local time)
function toLocalInputValue(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Parses "YYYY-MM-DDTHH:MM" (local) and returns an ISO string in UTC
function localToIsoUTC(local) {
  if (!local) return null;
  const d = new Date(local);
  if (isNaN(d.getTime())) return null;
  return d.toISOString();
}

// Formats an ISO string to human-friendly Polish local datetime
function formatDue(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString("pl-PL", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function statusPill(r) {
  const now = new Date();
  const due = new Date(r.due_at);
  const snooze = r.snooze_until ? new Date(r.snooze_until) : null;
  if (r.status === "done") return { label: "Zrealizowane", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" };
  if (r.status === "snoozed" && snooze && snooze > now) {
    return { label: `Przełożone → ${formatDue(r.snooze_until)}`, cls: "bg-amber-100 text-amber-800 border-amber-200" };
  }
  if (due <= now) return { label: "Do zrobienia", cls: "bg-rose-100 text-rose-800 border-rose-200" };
  return { label: "Zaplanowane", cls: "bg-sky-100 text-sky-800 border-sky-200" };
}


export default function PrzypomnieniaTab() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [showDone, setShowDone] = useState(false);
  const [creating, setCreating] = useState(false);

  const [form, setForm] = useState({
    title: "",
    note: "",
    dueLocal: "",
    target_employee_id: "", // "" = self
  });
  const [snoozing, setSnoozing] = useState(null); // { id, dueLocal }

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/przypomnienia`, {
        params: { include_done: showDone ? "true" : "false" },
      });
      setReminders(data || []);
    } catch (e) {
      console.error("load reminders failed", e);
    }
  }, [showDone]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const rem = axios.get(`${API}/przypomnienia`, { params: { include_done: "false" } });
        const emp = isAdmin ? axios.get(`${API}/employees`) : Promise.resolve({ data: [] });
        const [remRes, empRes] = await Promise.all([rem, emp]);
        if (cancelled) return;
        setReminders(remRes.data || []);
        setEmployees((empRes.data || []).filter((e) => e.role === "employee" || !e.role));
      } catch (e) {
        console.error("initial load failed", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isAdmin]);

  useEffect(() => { load(); }, [showDone, load]);

  // Deep-link: ?tab=przypomnienia&snooze=<id>
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("snooze");
    if (sid) {
      const now = new Date();
      now.setHours(now.getHours() + 1, 0, 0, 0);
      setSnoozing({ id: sid, dueLocal: toLocalInputValue(now) });
    }
  }, []);

  const pending = useMemo(() => reminders.filter((r) => r.status !== "done"), [reminders]);
  const done = useMemo(() => reminders.filter((r) => r.status === "done"), [reminders]);

  const resetForm = () => {
    const d = new Date();
    d.setHours(d.getHours() + 1, 0, 0, 0);
    setForm({ title: "", note: "", dueLocal: toLocalInputValue(d), target_employee_id: "" });
  };

  const openCreate = () => {
    resetForm();
    setCreating(true);
  };

  const handleCreate = async () => {
    const iso = localToIsoUTC(form.dueLocal);
    if (!form.title.trim() || !iso) {
      alert("Uzupełnij tytuł i datę");
      return;
    }
    try {
      await axios.post(`${API}/przypomnienia`, {
        title: form.title.trim(),
        note: form.note.trim() || null,
        due_at: iso,
        target_employee_id: form.target_employee_id || null,
      });
      setCreating(false);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Nie udało się dodać przypomnienia");
    }
  };

  const handleDone = async (id) => {
    try {
      await axios.patch(`${API}/przypomnienia/${id}/done`);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Nie udało się oznaczyć jako zrealizowane");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Usunąć przypomnienie?")) return;
    try {
      await axios.delete(`${API}/przypomnienia/${id}`);
      setReminders((prev) => prev.filter((r) => r.id !== id));
    } catch (e) {
      alert(e?.response?.data?.detail || "Nie udało się usunąć");
    }
  };

  const openSnooze = (id) => {
    const now = new Date();
    now.setHours(now.getHours() + 1, 0, 0, 0);
    setSnoozing({ id, dueLocal: toLocalInputValue(now) });
  };

  const handleSnoozeSave = async () => {
    if (!snoozing) return;
    const iso = localToIsoUTC(snoozing.dueLocal);
    if (!iso) { alert("Wybierz datę"); return; }
    try {
      await axios.patch(`${API}/przypomnienia/${snoozing.id}/snooze`, {
        snooze_until: iso,
      });
      setSnoozing(null);
      // Clean up the ?snooze= param
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete("snooze");
        window.history.replaceState({}, "", url.toString());
      } catch { /* ignore */ }
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Nie udało się przełożyć");
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-slate-500">Ładowanie przypomnień...</div>;
  }

  return (
    <div className="max-w-[1200px] mx-auto px-3 py-4 sm:px-6 sm:py-6 space-y-4 sm:space-y-6" data-testid="przypomnienia-container">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl sm:text-3xl font-bold text-slate-800 flex items-center gap-2">
            <BellRinging className="w-6 h-6 text-blue-700" weight="fill" />
            Przypomnienia
          </h2>
          <p className="text-xs sm:text-base text-slate-600 mt-1">
            {isAdmin
              ? "Twórz przypomnienia dla siebie lub pracowników. Powiadomienia push wysyłane co godzinę do momentu zrealizowania lub przełożenia."
              : "Twoje przypomnienia. Powiadomienie przychodzi co godzinę do momentu, aż klikniesz „Zrealizowane” lub przełożysz na inny termin."}
          </p>
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white rounded-md text-sm font-medium shadow-sm"
          data-testid="przypomnienia-new-btn"
        >
          <Plus className="w-4 h-4" weight="bold" /> Nowe przypomnienie
        </button>
      </div>

      {/* Active reminders */}
      {pending.length === 0 ? (
        <div className="bg-white rounded-xl p-8 text-center text-slate-500 shadow-sm border border-slate-200">
          Brak aktywnych przypomnień.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {pending.map((r) => {
            const pill = statusPill(r);
            return (
              <div
                key={r.id}
                className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 flex flex-col gap-3"
                data-testid={`przypomnienie-${r.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-800 truncate">{r.title}</div>
                    {r.note && (
                      <div className="text-xs text-slate-600 mt-1 whitespace-pre-wrap">{r.note}</div>
                    )}
                  </div>
                  <span className={`text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full border shrink-0 ${pill.cls}`}>
                    {pill.label}
                  </span>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                  <span className="inline-flex items-center gap-1">
                    <CalendarBlank className="w-3.5 h-3.5" />
                    {formatDue(r.due_at)}
                  </span>
                  {isAdmin && r.target_employee_name && (
                    <span className="inline-flex items-center gap-1">
                      <User className="w-3.5 h-3.5" />
                      {r.target_employee_name}
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 pt-1 border-t border-slate-100">
                  <button
                    onClick={() => handleDone(r.id)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md text-xs font-medium"
                    data-testid={`przypomnienie-done-${r.id}`}
                  >
                    <Check className="w-3.5 h-3.5" weight="bold" /> Zrealizowane
                  </button>
                  <button
                    onClick={() => openSnooze(r.id)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white rounded-md text-xs font-medium"
                    data-testid={`przypomnienie-snooze-${r.id}`}
                  >
                    <ClockClockwise className="w-3.5 h-3.5" weight="bold" /> Przełóż
                  </button>
                  <button
                    onClick={() => handleDelete(r.id)}
                    className="ml-auto inline-flex items-center gap-1 px-2.5 py-1.5 text-red-600 hover:bg-red-50 rounded-md text-xs"
                    title="Usuń"
                    data-testid={`przypomnienie-delete-${r.id}`}
                  >
                    <Trash className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Done section toggle */}
      <div className="pt-2">
        <button
          onClick={() => setShowDone((v) => !v)}
          className="text-sm text-slate-600 hover:text-slate-800 underline underline-offset-2"
          data-testid="przypomnienia-toggle-done"
        >
          {showDone ? "Ukryj zrealizowane" : "Pokaż zrealizowane"}
        </button>
      </div>
      {showDone && done.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {done.map((r) => (
            <div
              key={r.id}
              className="bg-slate-50 rounded-xl p-3 border border-slate-200 opacity-80"
              data-testid={`przypomnienie-done-item-${r.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-700 line-through truncate">{r.title}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {formatDue(r.due_at)}
                    {isAdmin && r.target_employee_name ? ` · ${r.target_employee_name}` : ""}
                  </div>
                </div>
                <button
                  onClick={() => handleDelete(r.id)}
                  className="p-1.5 text-red-600 hover:bg-red-100 rounded-md"
                  title="Usuń"
                >
                  <Trash className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create modal */}
      {creating && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6" data-testid="przypomnienia-create-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-800">Nowe przypomnienie</h3>
              <button onClick={() => setCreating(false)} className="p-1 hover:bg-slate-100 rounded-md">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Tytuł</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="np. Odebrać paczkę"
                  className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                  data-testid="przypomnienia-title"
                  autoFocus
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Data i godzina</label>
                <input
                  type="datetime-local"
                  value={form.dueLocal}
                  onChange={(e) => setForm({ ...form, dueLocal: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                  data-testid="przypomnienia-due"
                />
              </div>
              {isAdmin && (
                <div>
                  <label className="text-xs font-medium text-slate-600 block mb-1">Dla kogo</label>
                  <select
                    value={form.target_employee_id}
                    onChange={(e) => setForm({ ...form, target_employee_id: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
                    data-testid="przypomnienia-target"
                  >
                    <option value="">Dla mnie (Administrator)</option>
                    {employees.map((e) => (
                      <option key={e._id} value={e._id}>
                        {e.imie_nazwisko || e.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Notatka (opcjonalnie)</label>
                <textarea
                  value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                  placeholder="Dodatkowe informacje..."
                  rows={3}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm resize-none"
                  data-testid="przypomnienia-note"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={() => setCreating(false)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md text-sm"
                >
                  Anuluj
                </button>
                <button
                  onClick={handleCreate}
                  disabled={!form.title.trim() || !form.dueLocal}
                  className="px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white rounded-md text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="przypomnienia-create-submit"
                >
                  Zapisz
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Snooze modal */}
      {snoozing && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6" data-testid="przypomnienia-snooze-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-800">Przełóż przypomnienie</h3>
              <button
                onClick={() => setSnoozing(null)}
                className="p-1 hover:bg-slate-100 rounded-md"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-3">
              <p className="text-xs text-slate-500">
                Wybierz dzień i godzinę, na kiedy przenieść przypomnienie.
              </p>
              <input
                type="datetime-local"
                value={snoozing.dueLocal}
                onChange={(e) => setSnoozing({ ...snoozing, dueLocal: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                data-testid="przypomnienia-snooze-input"
              />
              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={() => setSnoozing(null)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md text-sm"
                >
                  Anuluj
                </button>
                <button
                  onClick={handleSnoozeSave}
                  disabled={!snoozing.dueLocal}
                  className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-md text-sm font-medium disabled:opacity-40"
                  data-testid="przypomnienia-snooze-submit"
                >
                  Przełóż
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
